import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSeo } from "@/lib/useSeo";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const sort = params.get("sort") || "";
  const category = params.get("category") || "";
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);

  useSeo({
    title: q ? `${q} — Stuffly` : category ? `${category} — Stuffly` : "Shop All Products — Stuffly",
    description: q
      ? `Search results for "${q}" on Stuffly. Fast NZ dispatch, secure Stripe checkout.`
      : category
        ? `Shop ${category} at Stuffly — curated NZ bargains with fast dispatch and secure Stripe checkout.`
        : "Browse the full Stuffly catalogue — phones, tools, electronics, camping, outdoor gear and more. Free shipping over $100 NZD.",
    path: q ? `/products?q=${encodeURIComponent(q)}` : category ? `/products?category=${encodeURIComponent(category)}` : "/products",
  });

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);
  useEffect(() => { setPage(1); }, [q, sort, category]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (sort) qs.set("sort", sort);
    if (category) qs.set("category", category);
    qs.set("page", page);
    qs.set("limit", 24);
    api.get(`/products?${qs.toString()}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [q, sort, category, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / 24));

  const setCategory = (name) => {
    const p = new URLSearchParams(params);
    if (name) p.set("category", name); else p.delete("category");
    setParams(p);
  };

  return (
    <div>
      <div className="border-b border-zinc-900 px-6 md:px-12 py-8 flex flex-col md:flex-row justify-between gap-4 md:items-end">
        <div>
          <div className="tag mb-3">Catalogue</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter uppercase font-[Outfit]" data-testid="products-heading">
            {q ? <>Results For "<span className="text-[#ff3333]">{q}</span>"</> : category ? <span className="text-[#ff3333]">{category}</span> : "All Products"}
          </h1>
          <div className="text-sm text-zinc-600 mt-2">{data.total.toLocaleString()} items · Honest NZD pricing</div>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="sort-select"
            value={sort}
            onChange={e => {
              const v = e.target.value;
              const p = new URLSearchParams(params);
              if (v) p.set("sort", v); else p.delete("sort");
              setParams(p);
            }}
            className="input-brutal text-xs uppercase tracking-widest font-bold font-[Outfit]"
          >
            <option value="">Newest</option>
            <option value="price-asc">Price ↑</option>
            <option value="price-desc">Price ↓</option>
            <option value="title">A → Z</option>
          </select>
        </div>
      </div>

      {cats.length > 0 && (
        <div className="border-b border-zinc-900 px-6 md:px-12 py-3 flex gap-2 overflow-x-auto" data-testid="category-bar">
          <button
            data-testid="category-chip-all"
            onClick={() => setCategory("")}
            className={`px-3 py-1.5 border text-[0.65rem] uppercase tracking-widest font-bold font-[Outfit] whitespace-nowrap transition-colors ${!category ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}
          >
            All
          </button>
          {cats.map(c => (
            <button
              key={c.name}
              data-testid={`category-chip-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setCategory(c.name)}
              className={`px-3 py-1.5 border text-[0.65rem] uppercase tracking-widest font-bold font-[Outfit] whitespace-nowrap transition-colors ${category === c.name ? "bg-[#ff3333] text-white border-[#ff3333]" : "border-zinc-300 hover:border-zinc-900"}`}
            >
              {c.name} <span className="opacity-50">{c.count}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-xs uppercase tracking-widest font-bold font-[Outfit]" data-testid="products-loading">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="p-16 text-center">
          <div className="text-2xl font-black uppercase tracking-tighter font-[Outfit]">Nothing Found</div>
          <p className="mt-2 text-sm text-zinc-600">Try a different search or check back later.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 grid-borders border-l border-zinc-900 border-b" data-testid="products-grid">
          {data.items.map(p => <ProductCard key={p.id} p={p} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 p-8">
          <button data-testid="page-prev" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn-outline disabled:opacity-30">← Prev</button>
          <span className="self-center text-xs uppercase tracking-widest font-bold font-[Outfit] px-4">Page {page} / {totalPages}</span>
          <button data-testid="page-next" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="btn-outline disabled:opacity-30">Next →</button>
        </div>
      )}
    </div>
  );
}
